import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Dispatch, MouseEvent, SetStateAction, useReducer } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as VerticalDotsIcon } from '../../../../../../../assets/vertical-dots.svg';
import RoutePaths from '../../../../../../../routes/RoutePaths';
import { useAppSelector } from '../../../../../../../store/hooks';
import zeplinColor from '../../../../../../../theme/Color';

import { initialAlertState, useAlertStatus } from '../../../../../../../customHook/useAlertStatus';
import { Role, RolesTableColumns } from '../../../../../../../model/rolesAndPerms.model';
import { modalReducer } from '../../../../../../../reducers/modalReducer';
import CustomAlert from '../../../../../../shared/CustomAlert/CustomAlert';
import CustomButton from '../../../../../../shared/CustomButton/CustomButton';
import { RoleHistory } from '../RoleHistory';
import { RoleSettings } from '../RoleSettings';
import styles from './RolesTable.module.css';
import { ModifiedByCell } from './components/ModifiedByCell';
import { RoleNameCell } from './components/RoleNameCell';
import { UsersAssignedCell } from './components/UsersAssigned';

type RolesTableProps = {
  roleCardElement: SVGSVGElement | null;
  handleOpenSettings: (event: MouseEvent<SVGSVGElement>, role: Role) => void;
  setRoleCardElement: Dispatch<SetStateAction<SVGSVGElement | null>>;
};

export const RolesTable = ({
  roleCardElement,
  handleOpenSettings,
  setRoleCardElement,
}: RolesTableProps) => {
  const roles = useAppSelector((state) => state.rolesAndPermissions.roles);
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    history: false,
  });
  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const onHistoryClick = async () => {
    modalDispatch({ type: 'OPEN', payload: 'history' });
  };

  return (
    <Box>
      <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none', marginTop: '24px' }}>
        <TableContainer
          sx={{
            overflow: 'scroll',
            height: { xs: 'calc(100vh - 190px)', xl: 'calc(100vh - 250px)' },
          }}
        >
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow
                sx={{
                  border: '1px solid black',
                  borderRadius: '14px',
                  '& th': {
                    backgroundColor: '#f5f5f5',
                  },
                  '& th:first-child': {
                    borderRadius: '14px 0px 0px 14px',
                  },
                  '& th:last-child': {
                    borderRadius: '0px 14px 14px 0px',
                  },
                }}
              >
                {RolesTableColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((row, ind) => {
                return (
                  <TableRow
                    hover
                    role='checkbox'
                    tabIndex={-1}
                    component={Link}
                    to={`${RoutePaths.Home}/admin/role/${row.roleId}`}
                    key={ind}
                    sx={{
                      '&:last-child td, &:last-child th': { borderBottom: '2px solid #ddd' },
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    {RolesTableColumns.map((column) => {
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.id === 'actions' ? (
                            <Box
                              sx={{
                                display: 'inline-flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: '24px',
                              }}
                            >
                              <CustomButton
                                variant='text'
                                buttonVariant='buttonMedium'
                                text={'Role History'}
                                onClick={(e: Event) => {
                                  e.preventDefault();
                                  onHistoryClick();
                                }}
                                disabled={false}
                              />
                              <Box
                                sx={{
                                  padding: '3px',
                                  borderRadius: '50%',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  ':hover': {
                                    backgroundColor: zeplinColor.Neutral5,
                                    cursor: 'pointer',
                                  },
                                }}
                              >
                                <VerticalDotsIcon
                                  className={styles['vertical-icon']}
                                  onClick={(e: MouseEvent<SVGSVGElement>) => {
                                    e.preventDefault();
                                    handleOpenSettings(e, row);
                                  }}
                                />
                              </Box>
                            </Box>
                          ) : column.id === 'userCount' ? (
                            <UsersAssignedCell row={row} column={column} />
                          ) : column.id === 'updatedBy' ? (
                            <ModifiedByCell row={row} column={column} />
                          ) : column.id === 'roleName' ? (
                            <RoleNameCell row={row} column={column} />
                          ) : column.id === 'lastModified' && row.updatedAt ? (
                            row.updatedAt
                          ) : (
                            'No record'
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <RoleSettings
        anchorElement={roleCardElement}
        onClose={() => setRoleCardElement(null)}
        isRow
        startOperation={startOperation}
        completeOperation={completeOperation}
      />
      <RoleHistory isModalOpen={modalState.history} modalDispatch={modalDispatch} />
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};
